import { useCallback, useEffect, useState } from "react";
import { matchPath, useLocation, useParams } from "react-router-dom";
import { useSelector, useDispatch } from "react-redux";
import { focusManager, useQueryClient } from "@tanstack/react-query";

import { setActiveCase } from "../../../actions/dashboardActions";
import { dashboardQueryOptions, dashboardRefetchInterval, fetchDashboard } from "../../../shared/dashboard";

export const useSelectedCaseRef = ({ caseScreen } = {}) => {
  const { caseRef } = useParams();

  const lastActiveCaseRef = useSelector((state) => state.dashboardState.lastActiveCaseRef);
  const lastActiveCaseScreen = useSelector((state) => state.dashboardState.lastActiveCaseScreen);
  const dispatch = useDispatch();

  // Store the active case and screen in Redux so we can link back to the case
  // page from URLs that don't include the :caseRef param.
  useEffect(() => {
    // Do nothing if we don't have a reference in the URL and a screen specified.
    if (!caseRef || !caseScreen) {
      return;
    }

    if (caseRef !== lastActiveCaseRef || caseScreen !== lastActiveCaseScreen) {
      dispatch(setActiveCase(caseRef, caseScreen));
    }
  }, [dispatch, caseRef, caseScreen, lastActiveCaseRef, lastActiveCaseScreen]);

  if (!!caseRef) {
    return caseRef;
  }
  return lastActiveCaseRef;
};

export const useSelectedCase = ({ caseScreen } = {}) => {
  const caseRef = useSelectedCaseRef({ caseScreen: caseScreen });

  const selectedCase = useSelector((state) => {
    if (!state.dashboardState.cases) {
      return null;
    }
    return state.dashboardState.cases.find((c) => c.case_reference === caseRef);
  });

  return selectedCase;
};

// Loads the dashboard when a page mounts, unless the cached data is newer than staleTime.
// DashboardSelector copies the response into Redux and handles a failure.
export const useRefreshDashboard = ({ staleTime, enabled = true }) => {
  const queryClient = useQueryClient();

  useEffect(() => {
    if (!enabled) return;
    queryClient.prefetchQuery({ ...dashboardQueryOptions, queryFn: () => fetchDashboard(), staleTime });
  }, [queryClient, staleTime, enabled]);
};

// The refetchInterval for the dashboard query. It backs off once the dashboard has
// been on screen for a while, measured from when it opened, the client last moved
// between dashboard pages or the tab last became visible. A new callback makes
// useQuery pick up the shorter interval straight away.
export const useDashboardRefetchInterval = () => {
  const { key } = useLocation();
  const [onScreenSince, setOnScreenSince] = useState(Date.now);

  useEffect(() => {
    setOnScreenSince(Date.now());
  }, [key]);

  useEffect(
    () =>
      focusManager.subscribe((isFocused) => {
        if (isFocused) setOnScreenSince(Date.now());
      }),
    [],
  );

  return useCallback(
    (query) =>
      dashboardRefetchInterval(query.state.data, {
        onScreenFor: Date.now() - onScreenSince,
        failed: query.state.status === "error",
      }),
    [onScreenSince],
  );
};

// The case the URL belongs to. ClientDashboard sits above its Routes, so this reads the path itself; a
// doable page names no case, so its case is the one holding it.
export const useCaseFromPath = () => {
  const { pathname } = useLocation();
  const cases = useSelector((state) => state.dashboardState.cases);

  const doablePage = matchPath("/dashboard/doable/:doableId", pathname);
  const casePage = matchPath("/dashboard/:caseRef/*", pathname);
  return doablePage
    ? cases?.find((c) => c.doables?.some((d) => d.id === doablePage.params.doableId))
    : cases?.find((c) => c.case_reference === casePage?.params.caseRef);
};

export const useProbateTheme = () => useCaseFromPath()?.case_type === "probate";
